import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import {
  ChevronLeft,
  ChevronRight,
  X,
  DollarSign,
  Star,
  Package,
  Check,
  Minus,
  Eye,
  ArrowLeftRight
} from 'lucide-react';
import { API_URL } from '../../config/api';
import { toast } from '../ui/use-toast';
import { cleanProductUrl } from '@/utils/url-cleaner';

interface ProductImage {
  url: string;
  alt: string;
  type?: string;
  width?: number;
  height?: number;
}

interface Product {
  id: string;
  url: string;
  title: string;
  name: string;
  brand: string;
  category: string;
  subcategories: string[];
  price: number;
  currency: string;
  priceText: string;
  availability: string;
  rating: number;
  reviewCount: number;
  mainImage: ProductImage;
  additionalImages?: ProductImage[];
  features: string[];
  specifications: Record<string, any>;
  physical?: Record<string, any>;
  comparableFields: string[];
}

interface ComparisonData {
  products: Product[];
  comparableFields: string[];
  comparisonData: Record<string, any[]>;
}

interface ProductComparisonModalProps {
  isOpen: boolean;
  onClose: () => void;
  siteId: string;
  productIds: string[];
}

export const ProductComparisonModal: React.FC<ProductComparisonModalProps> = ({
  isOpen,
  onClose,
  siteId,
  productIds
}) => {
  const [comparisonData, setComparisonData] = useState<ComparisonData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState<Record<string, number>>({});


  useEffect(() => {
    if (isOpen && productIds.length >= 2) {
      fetchComparisonData();
    }
  }, [isOpen, productIds, siteId]);

  const fetchComparisonData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(
        `${API_URL}/catalog/sites/${siteId}/compare?products=${productIds.join(',')}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch comparison data');
      }

      const data = await response.json();
      
      // Fetch additional images for each product
      const productsWithImages = await Promise.all(
        data.products.map(async (product: Product) => {
          try {
            const detailsResponse = await fetch(
              `${API_URL}/catalog/sites/${siteId}/products/${product.id}`,
              {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                },
              }
            );
            
            if (detailsResponse.ok) {
              const details = await detailsResponse.json();
              return {
                ...product,
                additionalImages: details.additionalImages || []
              };
            }
          } catch (err) {
            console.error('Failed to fetch product details:', err);
          }
          return product;
        })
      );
      
      setComparisonData({
        ...data,
        products: productsWithImages
      });
      
      // Initialize image indices
      const initialIndices: Record<string, number> = {};
      productsWithImages.forEach(product => {
        initialIndices[product.id] = 0;
      });
      setActiveImageIndex(initialIndices);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load comparison data');
      toast({
        title: 'Error',
        description: 'Failed to load product comparison',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getProductImages = (product: Product): ProductImage[] => {
    const images: ProductImage[] = [];
    if (product.mainImage?.url) {
      images.push(product.mainImage);
    }
    if (product.additionalImages && product.additionalImages.length > 0) {
      images.push(...product.additionalImages);
    }
    return images;
  };

  const handleImageNavigation = (productId: string, direction: 'prev' | 'next') => {
    const product = comparisonData?.products.find(p => p.id === productId);
    if (!product) return;
    
    const images = getProductImages(product);
    const currentIndex = activeImageIndex[productId] || 0;
    
    if (direction === 'prev') {
      setActiveImageIndex(prev => ({
        ...prev,
        [productId]: currentIndex > 0 ? currentIndex - 1 : images.length - 1
      }));
    } else {
      setActiveImageIndex(prev => ({
        ...prev,
        [productId]: currentIndex < images.length - 1 ? currentIndex + 1 : 0
      }));
    }
  };

  const getAvailabilityBadge = (availability: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      in_stock: 'default',
      limited: 'secondary',
      out_of_stock: 'destructive',
      pre_order: 'outline',
      unknown: 'outline',
    };
    return (
      <Badge variant={variants[availability] || 'outline'}>
        {availability.replace(/_/g, ' ')}
      </Badge>
    );
  };

  const formatValue = (value: any): string => {
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (typeof value === 'object' && value !== null) {
      if (value.value !== undefined) {
        return String(value.value);
      }
      return JSON.stringify(value);
    }
    return String(value);
  };

  const getFieldDisplayName = (field: string): string => {
    return field
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-7xl h-[90vh] p-0 flex flex-col overflow-hidden" hideCloseButton>
        <DialogHeader className="p-6 pb-0 flex-shrink-0 border-b">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl font-bold">
              Compare Products
            </DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p>Loading comparison data...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="text-center py-8">
              <p className="text-destructive mb-4">{error}</p>
              <Button onClick={fetchComparisonData}>Retry</Button>
            </div>
          )}

          {comparisonData && !loading && !error && (
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="specifications">Specifications</TabsTrigger>
                <TabsTrigger value="features">Features</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-6">
                {/* Product Cards */}
                <div 
                  className="grid gap-4"
                  style={{ 
                    gridTemplateColumns: `repeat(${Math.min(comparisonData.products.length, 4)}, minmax(0, 1fr))` 
                  }}>
                  {comparisonData.products.map((product) => {
                    const images = getProductImages(product);
                    const currentImageIndex = activeImageIndex[product.id] || 0;
                    const currentImage = images[currentImageIndex] || product.mainImage;

                    return (
                      <Card key={product.id} className="overflow-hidden">
                        <CardContent className="p-0">
                          {/* Image Carousel */}
                          <div className="relative aspect-square bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 overflow-hidden">
                            {currentImage?.url ? (
                              <img
                                src={currentImage.url}
                                alt={currentImage.alt || product.name}
                                className="w-full h-full object-contain hover:scale-105 transition-transform duration-300"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package className="h-16 w-16 text-muted-foreground" />
                              </div>
                            )}
                            
                            {images.length > 1 && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 bg-background/80 backdrop-blur-sm"
                                  onClick={() => handleImageNavigation(product.id, 'prev')}
                                >
                                  <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 bg-background/80 backdrop-blur-sm"
                                  onClick={() => handleImageNavigation(product.id, 'next')}
                                >
                                  <ChevronRight className="h-4 w-4" />
                                </Button>
                                
                                {/* Image indicators */}
                                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                                  {images.map((_, idx) => (
                                    <div
                                      key={idx}
                                      className={`h-1.5 w-1.5 rounded-full transition-colors ${
                                        idx === currentImageIndex
                                          ? 'bg-primary'
                                          : 'bg-background/50'
                                      }`}
                                    />
                                  ))}
                                </div>
                              </>
                            )}
                          </div>

                          {/* Product Info */}
                          <div className="p-4 space-y-3">
                            <div>
                              <h3 className="font-semibold text-lg line-clamp-2">
                                {product.name}
                              </h3>
                              {product.brand && (
                                <p className="text-sm text-muted-foreground">
                                  {product.brand}
                                </p>
                              )}
                            </div>

                            <div className="flex items-center justify-between">
                              <div>
                                {product.price ? (
                                  <p className="text-2xl font-bold">
                                    {product.currency} {product.price.toLocaleString()}
                                  </p>
                                ) : (
                                  <p className="text-lg text-muted-foreground">
                                    {product.priceText || 'Price not available'}
                                  </p>
                                )}
                              </div>
                              {getAvailabilityBadge(product.availability)}
                            </div>

                            {product.rating && (
                              <div className="flex items-center gap-2">
                                <div className="flex items-center">
                                  <Star className="h-4 w-4 fill-current text-yellow-400" />
                                  <span className="ml-1 font-medium">{product.rating}</span>
                                </div>
                                {product.reviewCount && (
                                  <span className="text-sm text-muted-foreground">
                                    ({product.reviewCount} reviews)
                                  </span>
                                )}
                              </div>
                            )}

                            <div className="pt-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full"
                                onClick={() => window.open(cleanProductUrl(product.url), '_blank')}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                View Product
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Key Differences */}
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <ArrowLeftRight className="h-4 w-4" />
                      Key Differences
                    </h3>
                    <div className="space-y-3">
                      {/* Price Comparison */}
                      <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                        <span className="font-medium">Price Range</span>
                        <div className="flex gap-4">
                          {comparisonData.products.map((product) => (
                            <div key={product.id} className="text-center">
                              <p className="text-sm text-muted-foreground">
                                {product.name.split(' ')[0]}
                              </p>
                              <p className="font-semibold">
                                {product.price
                                  ? `${product.currency} ${product.price}`
                                  : 'N/A'}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Rating Comparison */}
                      <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                        <span className="font-medium">Customer Rating</span>
                        <div className="flex gap-4">
                          {comparisonData.products.map((product) => (
                            <div key={product.id} className="text-center">
                              <p className="text-sm text-muted-foreground">
                                {product.name.split(' ')[0]}
                              </p>
                              <div className="flex items-center gap-1">
                                <Star className="h-4 w-4 fill-current text-yellow-400" />
                                <span className="font-semibold">
                                  {product.rating || 'N/A'}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="specifications">
                <Card>
                  <CardContent className="pt-6">
                    <div className="w-full">
                      <table className="w-full">
                        <thead>
                          <tr>
                            <th className="text-left p-2 font-semibold">
                              Specification
                            </th>
                            {comparisonData.products.map((product) => (
                              <th key={product.id} className="text-left p-2">
                                {product.name}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {comparisonData.comparableFields.map((field, idx) => {
                            const values = comparisonData.comparisonData[field];
                            const hasVariation = values.some(
                              (v, i) => i > 0 && formatValue(v) !== formatValue(values[0])
                            );

                            return (
                              <tr
                                key={field}
                                className={`border-t ${
                                  hasVariation ? 'bg-accent/5' : ''
                                }`}
                              >
                                <td className="p-2 font-medium">
                                  {getFieldDisplayName(field)}
                                </td>
                                {values.map((value, productIdx) => (
                                  <td
                                    key={`${field}-${productIdx}`}
                                    className="p-2"
                                  >
                                    {value === 'N/A' ? (
                                      <Minus className="h-4 w-4 text-muted-foreground" />
                                    ) : typeof value === 'boolean' ? (
                                      value ? (
                                        <Check className="h-4 w-4 text-green-500" />
                                      ) : (
                                        <X className="h-4 w-4 text-red-500" />
                                      )
                                    ) : (
                                      formatValue(value)
                                    )}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="features">
                <div 
                  className="grid gap-4"
                  style={{ 
                    gridTemplateColumns: `repeat(${Math.min(comparisonData.products.length, 4)}, minmax(0, 1fr))` 
                  }}>
                  {comparisonData.products.map((product) => (
                    <Card key={product.id}>
                      <CardContent className="pt-6">
                        <h3 className="font-semibold mb-4">{product.name}</h3>
                        {product.features && product.features.length > 0 ? (
                          <ul className="space-y-2">
                            {product.features.map((feature, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                                <span className="text-sm">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            No features listed
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};